import React, { useEffect, useState } from "react";
import { getEndpointOrServiceName } from "../../services/dashboardService";
import {
  Activity,
  Copy,
  Hash,
  Link2,
  Loader2,
  Search,
  Server,
  X,
} from "lucide-react";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import { fetchVendorServiceUsage } from "../../services/customerService";

export default function ServiceUtility() {
  const [endpoint, setEndpoint] = useState("");
  const [serviceName, setServiceName] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [serviceList, setServiceList] = useState([]);
  const [backupServiceList, setBackupServiceList] = useState([]);
  const [debouncedServiceSearch, setDebouncedServiceSearch] = useState(null);
  const [showServiceList, setShowServiceList] = useState(false);

  const handleServiceNameChange = (e) => {
    setServiceName(e.target.value);
    setEndpoint("");
  };

  const handleEndpointChange = (e) => {
    setEndpoint(e.target.value);
    setServiceName("");
  };

  const fetchNameOrEndpoint = async () => {
    if (!endpoint && !serviceName) return;
    setLoading(true);

    try {
      const payload = {};

      if (endpoint) {
        payload.end_point = endpoint;
      } else {
        payload.service_name = serviceName;
      }

      // Mocking the API call behavior based on your provided handler
      const res = await getEndpointOrServiceName(payload);

      if (res?.status?.code === 200) {
        setData(res?.data);
        showSuccessToast(
          res?.message || res?.data?.message || "Data fetched successfully",
        );
      } else {
        showSuccessToast(
          res?.message || res?.data?.message || "Data fetching failed",
        );
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
      setShowServiceList(false);
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard
      .writeText(text)
      ?.then(showSuccessToast(`${text} copied`));
  };

  const loadServices = async () => {
    try {
      const response = await fetchVendorServiceUsage();

      if (response?.status?.code === 200) {
        setServiceList(
          Array.isArray(response.data?.services?.data)
            ? response.data?.services?.data
            : response.data.data || [],
        );
        setBackupServiceList(
          Array.isArray(response.data?.services?.data)
            ? response.data?.services?.data
            : response.data.data || [],
        );
      }
    } catch (error) {
      console.error("Failed to load services:", error);
      showErrorToast("Failed to load services");
    }
  };

  useEffect(() => {
    loadServices();
  }, []);

  // client side service search

  useEffect(() => {
    if (serviceName === "") setServiceList(backupServiceList);

    const target = serviceName?.toLowerCase()?.trim();

    const match = backupServiceList?.filter((item, idx) => {
      return item?.service_name?.toLowerCase().includes(target);
    });
    setServiceList(match);
  }, [serviceName]);

  return (
    <div className="w-full bg-white rounded-2xl border border-slate-200 p-6 mb-4 shadow-sm">
      {/* Header Section */}
      <div className="flex items-center gap-4 mb-2 pb-5 border-b border-slate-100/80">
        {/* Icon Container with subtle glow */}
        <div className="relative flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-slate-800 to-[#0B1736] shadow-md flex items-center justify-center border border-slate-700 overflow-hidden">
          <div className="absolute inset-0 bg-[#EA580C]/20 filter blur-md rounded-xl"></div>
          <Search
            className="relative z-10 text-[#F97316]"
            size={22}
            strokeWidth={2.5}
          />
        </div>

        {/* Title and Subtitle */}
        <div>
          <h2 className="text-xl font-extrabold text-slate-800 tracking-tight">
            Service Quick Look
          </h2>
          <p className="text-xs font-medium text-slate-500 flex items-center gap-1.5 mt-1">
            <Activity size={14} className="text-[#1E3A8A]" />
            Search and fetch endpoint details instantly
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
        {/* <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase mb-2">
            Service Name
          </label>
          <input
            type="text"
            value={serviceName}
            onChange={handleServiceNameChange}
            placeholder="e.g. Challan Pro"
            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-slate-700 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
          />
        </div> */}

        <div className="">
          <label
            htmlFor="service_name"
            className="block text-xs font-semibold text-slate-500 uppercase mb-2"
          >
            Service Name
          </label>
          <input
            onFocus={() => setShowServiceList(true)}
            // onBlur={() => setShowServiceList(false)}
            type="search"
            autoComplete="off"
            name="service_name"
            id="service_name"
            placeholder="Service Name"
            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-slate-700 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
            value={serviceName}
            onChange={(e) => {
              setServiceName(e.target.value);
            }}
          />

          {/* Dropdown Container */}
          {serviceList?.length > 0 && showServiceList && (
            <div className="relative">
              <div className="absolute top-full left-0 mt-2 w-72 z-30 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100 dark:border-zinc-800">
                  <h2 className="text-sm font-semibold text-zinc-700 dark:text-zinc-300">
                    Select Service
                  </h2>
                  <button
                    onClick={() => setShowServiceList(false)}
                    className="p-1 rounded-lg text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 hover:text-zinc-600 dark:hover:text-zinc-200 transition-colors"
                    aria-label="Close menu"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Service List Options */}
                <div className="py-1 max-h-60 overflow-y-auto divide-y divide-zinc-50 dark:divide-zinc-800/50">
                  {serviceList.map((item, idx) => (
                    <button
                      key={item?.id || idx} // Highly recommended to use a unique ID instead of index if available
                      onClick={() => {
                        setServiceName(item?.service_name);
                        // setFilters(prev => ({...prev, service_id: item.id}))
                        setShowServiceList(false);
                      }}
                      className="w-full text-left px-4 py-2.5 text-sm text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-800/60 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors block layout-fixed truncate"
                    >
                      {item?.service_name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase mb-2">
            Endpoint
          </label>
          <input
            type="text"
            value={endpoint}
            onChange={handleEndpointChange}
            placeholder="e.g. /srv2/..."
            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-slate-700 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
          />
        </div>

        <button
          onClick={fetchNameOrEndpoint}
          disabled={loading || (!endpoint && !serviceName)}
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 py-2.5 font-semibold text-white shadow-sm transition-all duration-200 hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Searching...
            </>
          ) : (
            <>
              <Search size={18} />
              Fetch Service Details
            </>
          )}
        </button>
      </div>

      {/* Result Cards */}
      {data && (
        <div className="w-full flex justify-center">
          <div className="mt-6 w-full  max-w-2xl space-y-4">
            {data.map((item) => (
              <div
                key={item.id}
                className="relative overflow-hidden rounded-xl border border-[#1E3A8A]/30 bg-gradient-to-br from-[#0B1736] via-[#09132d] to-[#040814] p-4 shadow-[0_10px_30px_-10px_rgba(11,23,54,0.45)] transition-all duration-300  hover:shadow-[0_15px_35px_-10px_rgba(11,23,54,0.6)] group"
              >
                {/* Background Effects */}
                <div className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-[#EA580C] opacity-20 blur-3xl group-hover:opacity-30 transition-opacity" />
                <div className="absolute -bottom-10 -left-10 h-28 w-28 rounded-full bg-[#1E3A8A] opacity-40 blur-3xl" />
                <div className="absolute inset-0 rounded-xl border border-white/10 pointer-events-none" />

                <div className="relative z-10 space-y-3">
                  {/* Header */}
                  {/* Header */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start  gap-2 flex-1 min-w-0">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#1E3A8A]/40 text-blue-300">
                        <Server size={17} strokeWidth={2.4} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
                          Service Module
                        </p>

                        <div className="flex items-center gap-2">
                          <h3 className=" truncate text-lg font-bold text-white leading-tight">
                            {item.service_name}
                          </h3>

                          <button
                            onClick={() => handleCopy(item?.service_name)}
                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-white/10 bg-white/5 text-slate-300 transition hover:border-orange-500/40 hover:bg-orange-500/10 hover:text-orange-400"
                          >
                            <Copy size={15} />
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-1 rounded-full border border-[#EA580C]/30 bg-[#EA580C]/15 px-2.5 py-1 shrink-0">
                      <Hash size={12} className="text-orange-400" />
                      <span className="text-[10px] font-bold tracking-wider text-orange-300">
                        {item.service_code}
                      </span>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

                  {/* Footer */}
                  {/* Footer */}
                  <div className="flex items-center justify-between gap-3 rounded-lg border border-white/5 bg-black/25 px-3 py-2">
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#EA580C]/20 text-orange-400">
                        <Link2 size={15} />
                      </div>

                      <div className="min-w-0 ">
                        <p className="text-[9px] uppercase tracking-[0.2em] text-slate-400">
                          Endpoint
                        </p>

                        <p className="truncate font-mono text-xs text-slate-200">
                          {item.end_point}
                        </p>
                      </div>

                      <button
                        onClick={() => handleCopy(item?.end_point)}
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-white/10 bg-white/5 text-slate-300 transition hover:border-orange-500/40 hover:bg-orange-500/10 hover:text-orange-400"
                      >
                        <Copy size={15} />
                      </button>
                    </div>
                    <div className="rounded-md bg-white/5 px-2 py-1 shrink-0">
                      <span className="text-[10px] font-semibold text-slate-300">
                        #{item.id}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
